// frontend/src/admin/App.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { getReviewQueueCount } from "./api";
import AdminProtectedRoute from "./AdminProtectedRoute";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOrgsPage from "./pages/AdminOrgsPage";
import AdminOrgDetailPage from "./pages/AdminOrgDetailPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminVouchersPage from "./pages/AdminVouchersPage";
import AdminPricingPage from "./pages/AdminPricingPage";
import AdminBannerPage from "./pages/AdminBannerPage";
import AdminVoucherDetailPage from "./pages/AdminVoucherDetailPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AdminCommunicationsPage from "./pages/AdminCommunicationsPage";
import AdminKnowledgePage from "./pages/AdminKnowledgePage";
import AdminTicketsPage from "./pages/AdminTicketsPage";
import AdminTicketDetailPage from "./pages/AdminTicketDetailPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import PromptManagementPage from "./pages/PromptManagementPage";
import AdminAiModelsPage from "./pages/AdminAiModelsPage";
import AdminConsentPage from "./pages/AdminConsentPage";

/**
 * Admin shell — a self-contained operator panel mounted at /admin/*.
 *
 * Deliberately NOT sharing the main app's Layout: it has its own dark-themed
 * sidebar so the operator always knows, at a glance, that they're in admin
 * mode (and not accidentally acting inside a tenant's account).
 *
 * Pages are filled in by P13-G12…G16; until then each route shows a
 * placeholder so the shell, routing, and access control are exercisable now.
 */

const NAV = [
  { to: "/admin", end: true, label: "Dashboard" },
  { to: "/admin/orgs", label: "Organisations" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/vouchers", label: "Vouchers" },
  { to: "/admin/pricing", label: "Pricing & Plans" },
  { to: "/admin/announcements", label: "Announcements" },
  { to: "/admin/reports", label: "Reports" },
  { to: "/admin/communications", label: "Communications" },
  { to: "/admin/knowledge", label: "Knowledge Base" },
  { to: "/admin/tickets", label: "Support Tickets" },
  { to: "/admin/ai/review-queue", label: "AI Review", badge: "review" },
  { to: "/admin/ai/prompts", label: "AI Prompts" },
  { to: "/admin/ai/models", label: "AI Models" },
  { to: "/admin/consent", label: "Consent Log" },
];

function sideNavClass({ isActive }) {
  return [
    "block px-3 py-2 rounded-md text-sm font-medium transition-colors",
    isActive
      ? "bg-indigo-600 text-white"
      : "text-slate-300 hover:bg-slate-800 hover:text-white",
  ].join(" ");
}

/** The dark sidebar + content frame shared by every admin page. */
function AdminLayout() {
  const { user, logout } = useAuth();
  const [reviewCount, setReviewCount] = useState(0);

  const refreshReviewCount = () => {
    getReviewQueueCount()
      .then(setReviewCount)
      .catch(() => {
        /* a failed badge count must not break the admin shell */
      });
  };

  useEffect(() => {
    refreshReviewCount();
  }, []);

  return (
    // App-shell: viewport-tall, never scrolls itself — only <main> scrolls.
    <div className="h-dvh flex bg-slate-100 overflow-hidden">
      {/* Dark sidebar — the visual signal that this is admin mode. */}
      <aside className="hidden sm:flex flex-col w-56 shrink-0 bg-slate-900">
        <div className="h-14 flex items-center px-4 border-b border-slate-700">
          <span className="text-lg font-bold text-white tracking-tight">
            QRFlow
          </span>
          <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-indigo-400 border border-indigo-500/40 rounded px-1.5 py-0.5">
            Admin
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map(({ to, end, label, badge }) => (
            <NavLink key={to} to={to} end={end} className={sideNavClass}>
              <span className="flex items-center justify-between">
                {label}
                {badge === "review" && reviewCount > 0 && (
                  <span
                    data-testid="review-badge"
                    className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white"
                  >
                    {reviewCount}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-700">
          <a
            href="/"
            className="block px-3 py-2 text-xs text-slate-400 hover:text-white transition-colors"
          >
            ← Exit admin
          </a>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="h-14 bg-slate-800 flex items-center justify-between px-4 shrink-0">
          <span className="sm:hidden text-base font-bold text-white">
            QRFlow Admin
          </span>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-slate-300 truncate max-w-xs">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={logout}
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Log out
            </button>
          </div>
        </header>

        <main className="flex-1 min-h-0 p-6 overflow-y-auto">
          <Routes>
            <Route index element={<AdminDashboard />} />
            <Route path="orgs" element={<AdminOrgsPage />} />
            <Route path="orgs/:orgId" element={<AdminOrgDetailPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="vouchers" element={<AdminVouchersPage />} />
            <Route
              path="vouchers/:voucherId"
              element={<AdminVoucherDetailPage />}
            />
            <Route path="pricing" element={<AdminPricingPage />} />
            <Route path="announcements" element={<AdminBannerPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route
              path="communications"
              element={<AdminCommunicationsPage />}
            />
            <Route path="knowledge" element={<AdminKnowledgePage />} />
            <Route path="tickets" element={<AdminTicketsPage />} />
            <Route path="tickets/:id" element={<AdminTicketDetailPage />} />
            <Route
              path="ai/review-queue"
              element={<ReviewQueuePage onReviewed={refreshReviewCount} />}
            />
            <Route path="ai/prompts" element={<PromptManagementPage />} />
            <Route path="ai/models" element={<AdminAiModelsPage />} />
            <Route path="consent" element={<AdminConsentPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/**
 * AdminApp — the admin entry point mounted at /admin/* in the main router.
 * Guards access (superadmin only), then renders the dark-themed shell.
 */
function AdminApp() {
  return (
    <AdminProtectedRoute>
      <AdminLayout />
    </AdminProtectedRoute>
  );
}

export default AdminApp;
